import { Store } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCheckPermission } from '@/hooks/useCheckPermission';
import { formatOrderPrice } from './order-totals';

const PAID_ORDER_TYPES = new Set(['click-collect']);

/**
 * Detail view for a single order. Mirrors the visual shape of
 * `fresh-dashboard/src/components/orders/OrderDetailModal.tsx`:
 *
 *   shop strip  →  Order #ref heading  →  product lines  →
 *   price  →  PAID ONLINE / PAY IN STORE  →  customer block  →  note
 *
 * Every product line renders (multi-line orders arrived with NEA-6025), with
 * a "{n} refunded" annotation on partially refunded lines; the total is net
 * of refunds.
 *
 * Action buttons are gated on `org:orders:manage`. "Mark complete" is shown
 * for `confirmed` and `partially_refunded` (the customer collects the
 * remainder); it posts immediately via `onComplete`. "Cancel order" is shown
 * for `reserved` / `paid` / `confirmed` / `partially_refunded` (cancelling a
 * partially refunded order refunds the remaining balance); clicking it defers
 * to `onCancel`, which the parent uses to open the email modal. "Refund items"
 * (paid-online orders that are `confirmed` / `partially_refunded`) defers to
 * `onRefundItems`, which opens the refund modal straight in selectable mode.
 */
export function OrderDetailModal({ order, shop, open, onOpenChange, onConfirm, onComplete, onCancel, onRefundItems }) {
	const canManage = useCheckPermission('org:orders:manage');

	if (!order) return null;

	const products = order.products || [];
	const orderRef = order.pickupReference || order.id;
	const paidOnline = PAID_ORDER_TYPES.has(order.orderType);
	const paymentLabel = paidOnline ? 'PAID ONLINE' : 'PAY IN STORE';
	const paymentClass = paidOnline ? 'text-success' : 'text-action';

	const canConfirm = order.status === 'reserved' || order.status === 'paid';
	const canMarkComplete = order.status === 'confirmed' || order.status === 'partially_refunded';
	const canCancel =
		order.status === 'reserved' || order.status === 'paid' || order.status === 'confirmed' || order.status === 'partially_refunded';
	const canRefundItems = paidOnline && (order.status === 'confirmed' || order.status === 'partially_refunded');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="rounded-2xl sm:max-w-md">
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted">
						<Store className="h-3.5 w-3.5" />
					</span>
					{shop?.name}
				</div>

				<DialogTitle className="mt-1 text-2xl font-extrabold tracking-tight">Order {orderRef}</DialogTitle>

				<DialogDescription asChild>
					<div className="space-y-3 pt-1">
						<div className="space-y-2">
							{products.map((p, i) => (
								<div key={p.barcode || i}>
									<div className="text-lg font-semibold leading-snug text-foreground">
										{Number(p.quantity) || 1} x {p.name || 'Order'}
										{Number(p.refundedQuantity) > 0 ? (
											<span className="ml-2 text-sm font-medium text-muted-foreground">
												{p.refundedQuantity} refunded
											</span>
										) : null}
									</div>
									{p.barcode ? <div className="text-sm text-muted-foreground">{p.barcode}</div> : null}
								</div>
							))}
						</div>
						<div className="text-base font-semibold text-foreground">{formatOrderPrice(order)}</div>
						<div className={`text-sm font-bold tracking-wide ${paymentClass}`}>{paymentLabel}</div>
					</div>
				</DialogDescription>

				<div className="my-2 border-t border-border/60" />

				<div className="space-y-1">
					{order.customerName ? <div className="text-base font-bold text-foreground">{order.customerName}</div> : null}
					{order.customerEmail ? <div className="text-sm text-muted-foreground">{order.customerEmail}</div> : null}
					{order.customerPhone ? <div className="text-sm text-muted-foreground">{order.customerPhone}</div> : null}
				</div>

				{order.notes ? (
					<div className="mt-3 rounded-lg bg-muted p-3">
						<div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Customer note</div>
						<div className="text-sm text-foreground">{order.notes}</div>
					</div>
				) : null}

				<div className="flex justify-end gap-2 pt-3">
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary"
					>
						Close
					</button>
					{canCancel ? (
						<button
							type="button"
							disabled={!canManage}
							onClick={() => onCancel?.(order)}
							className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
							title={canManage ? undefined : "You don't have permission to manage orders"}
						>
							Cancel order
						</button>
					) : null}
					{canRefundItems ? (
						<button
							type="button"
							data-testid="detail-refund-items"
							disabled={!canManage}
							onClick={() => onRefundItems?.(order)}
							className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
							title={canManage ? undefined : "You don't have permission to manage orders"}
						>
							Refund items
						</button>
					) : null}
					{canConfirm ? (
						<button
							type="button"
							data-testid="detail-confirm-order"
							disabled={!canManage}
							onClick={() => onConfirm?.(order)}
							className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
							title={canManage ? undefined : "You don't have permission to manage orders"}
						>
							Confirm order
						</button>
					) : null}
					{canMarkComplete ? (
						<button
							type="button"
							disabled={!canManage}
							onClick={() => onComplete?.(order)}
							className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
							title={canManage ? undefined : "You don't have permission to manage orders"}
						>
							Mark complete
						</button>
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	);
}
